import { useCallback, useMemo, useState } from "react";
import { applyPagination } from "../../utils/applyPagination";
import Sidepane from "../components/Sidepane";
import { useSidePane } from "../hook/useSidePane";
import type { ILink } from "../../entities/link.entity";
import LinkTable from "../components/LinkTable";
import LinkDetail from "../components/LinkDetail";
import { useLinkRemover } from "../hook/useLinkRemover";
import { useLinksList } from "../hook/useLinksList";
import { CrudContainer } from "../components/CrudContainer";

const useLinksPaginated = (
	page: number,
	rowsPerPage: number,
	data: unknown,
) => {
	return useMemo(() => {
		return applyPagination(data, page, rowsPerPage);
	}, [page, rowsPerPage, data]);
};

export default function Links({ collectionTitle = "Links" }) {
	const [linkDetail, setLinkDetail] = useState<ILink | undefined>(undefined);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);

	const linksQuery = useLinksList();
	const links = useLinksPaginated(page, rowsPerPage, linksQuery.data || []);
	const sidepane = useSidePane({
		onClose: () => setLinkDetail(undefined),
	});

	const linkRemover = useLinkRemover();

	const handlePageChange = useCallback<
		NonNullable<Parameters<typeof LinkTable>[0]["onPageChange"]>
	>((_, value: number) => {
		setPage(value);
	}, []);

	const handleRowsPerPageChange = useCallback<
		NonNullable<Parameters<typeof LinkTable>[0]["onRowsPerPageChange"]>
	>((event) => {
		setRowsPerPage(event.target.value);
	}, []);

	function handleUpdateLink(id: string) {
		if (!linksQuery.data) return;

		for (const link of linksQuery.data) {
			if (link.id === id) {
				setLinkDetail(link);
				sidepane.setOpen(true);
			}
		}
	}

	const handleLinkChanged = async () => {
		sidepane.setOpen(false);
	};

	return (
		<>
			<CrudContainer
				title={collectionTitle}
				onClickAdd={() => sidepane.setOpen(true)}
				subtitle="Gerencie os links do sistema"
				empty={!linksQuery.data}
			>
				{/* <TableSearch /> */}
				{links.length > 0 && (
					<LinkTable
						count={linksQuery.data?.length || 0}
						items={links}
						onPageChange={handlePageChange}
						onRowsPerPageChange={handleRowsPerPageChange}
						linkRemover={linkRemover}
						onUpdateLink={handleUpdateLink}
						page={page}
						rowsPerPage={rowsPerPage}
					/>
				)}
			</CrudContainer>

			<Sidepane open={sidepane.isOpen} toggle={sidepane.toggle}>
				<LinkDetail item={linkDetail} onLinkSaved={handleLinkChanged} />
			</Sidepane>
		</>
	);
}
